import type { EditProps } from "react-admin";
import {
  Edit,
  SimpleForm,
  DateInput,
  DeleteButton,
  Toolbar,
  SaveButton,
  ReferenceInput,
  SelectInput,
  TextInput,
  TopToolbar,
  ListButton,
  ShowButton,
  FormDataConsumer,
} from "react-admin";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { purposeHelperText } from "@/utils/purpose-helper-text";
import RazaItsLookup from "./components/RazaItsLookup";
import RazaSafaiUploadField from "./components/RazaSafaiUploadField";
import RazaRequestLagatSection from "./RazaRequestLagatSection";

const validateMohallah = (value: unknown, allValues?: Record<string, unknown>) => {
  const matched = allValues?.itsDirectoryMatched;
  if (matched === false) {
    const mohallah = typeof value === "string" ? value.trim() : "";
    if (!mohallah) return "Mohallah is required when ITS is not in directory";
  }
  return undefined;
};

const transform = (data: Record<string, unknown>) => {
  const requestDateRaw = data.requestDate;
  const requestDate =
    requestDateRaw instanceof Date ? requestDateRaw : new Date(String(requestDateRaw ?? ""));
  const payload: Record<string, unknown> = {
    purpose: data.purpose,
    itsNo: data.itsNo,
    name: data.name,
    mohallah: data.mohallah,
  };
  if (!Number.isNaN(requestDate.getTime())) {
    payload.requestDate = requestDate.toISOString();
  }
  if (data.itsDirectoryMatched === false) {
    const safaiUrl = typeof data.safaiUrl === "string" ? data.safaiUrl.trim() : "";
    payload.safaiUrl = safaiUrl || null;
  } else if (Object.prototype.hasOwnProperty.call(data, "safaiUrl")) {
    payload.safaiUrl = null;
  }
  return payload;
};

const CustomToolbar = () => (
  <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
    <SaveButton />
    <DeleteButton mutationMode="pessimistic" />
  </Toolbar>
);

const EditActions = () => (
  <TopToolbar>
    <ListButton />
    <ShowButton label="Raza request" />
  </TopToolbar>
);

const RazaRequestsEdit = (props: EditProps) => (
  <Edit
    {...props}
    actions={<EditActions />}
    transform={transform}
    mutationMode="pessimistic"
    redirect="show"
  >
    <>
      <SimpleForm warnWhenUnsavedChanges toolbar={<CustomToolbar />} sx={{ maxWidth: 700 }}>
        <RazaItsLookup source="itsNo" label="ITS no." fullWidth isRequired />
        <TextInput source="name" label="Name" fullWidth required />
        <FormDataConsumer>
          {({ formData }) =>
            formData?.itsDirectoryMatched === false ? (
              <>
                <TextInput
                  source="mohallah"
                  label="Mohallah"
                  fullWidth
                  validate={validateMohallah}
                  required
                />
                <RazaSafaiUploadField />
              </>
            ) : null
          }
        </FormDataConsumer>
        <ReferenceInput
          source="purpose"
          reference="bookingPurpose"
          label="Purpose"
          fullWidth
          required
        >
          <SelectInput
            optionText="name"
            optionValue="id"
            fullWidth
            helperText={purposeHelperText("razaRequest")}
          />
        </ReferenceInput>
        <DateInput source="requestDate" label="Request date" fullWidth required />
        <Divider sx={{ mb: 2 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
          Lagat receipts
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create, link, or remove receipts for this request. Changes apply immediately (no need to
          save the form above).
        </Typography>
        <RazaRequestLagatSection />
      </SimpleForm>
    </>
  </Edit>
);

export default RazaRequestsEdit;
